// @flow
import * as React from 'react';
import Section from './Section';
import { Box, Stack } from '@locus-taxy/neo-ui';
import { EnhancedField } from 'common/components/core/form/Field';
import CompartmentCapacity from './CompartmentCapacity';
import { VehicleCompartment } from 'common/models/internal/VehicleCompartment';
import type { ThemeType } from 'react-css-themr';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import type { TFunction } from 'react-i18next';

type Props = {
    compartment: VehicleCompartment,
    compartmentIndex: number,
    collapsed: boolean,
    onCollapse: (compartmentName: string, collapsed: boolean) => void,
    onDelete: (compartmentName: string) => void,
    onDuplicate: (compartmentName: string) => void,
    onChange: (
        updatedCompartment: VehicleCompartment,
        compartmentIndex: number
    ) => void,
    theme: ThemeType,
    unitConverter: UnitConversionHelper,
    t: TFunction
};

export const VehicleCompartmentInput = ({
    compartment,
    compartmentIndex,
    onDelete,
    onDuplicate,
    onCollapse,
    collapsed,
    onChange,
    theme,
    unitConverter,
    t
}: Props): React.Node => {
    const compartmentFormTitle = compartment.getCompartmentName(
        compartmentIndex
    );
    const onChangeHandler = React.useCallback(
        (accessor, value) => {
            onChange(
                compartment.setIn(accessor.split('.'), value),
                compartmentIndex
            );
        },
        [compartment, compartmentIndex, onChange]
    );

    return (
        <Section
            id={compartmentFormTitle}
            title={compartmentFormTitle}
            onToggleCollapse={onCollapse}
            collapsed={collapsed}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
        >
            <Stack>
                <span className={theme.label}>{`${t(
                    'masterVehicleModelProperties.compartments.id'
                )}*`}</span>
                <EnhancedField
                    type="text"
                    element="input"
                    value={compartment.id}
                    onChange={value => onChangeHandler('id', value)}
                    testId="compartmentId"
                    fieldId="compartmentId"
                    placeholder={t(
                        'masterVehicleModelProperties.compartments.id'
                    )}
                    theme={theme}
                    error={compartment.getValidationMessage('id')}
                    required
                />
            </Stack>
            <Stack>
                <span className={theme.label}>{`${t(
                    'masterVehicleModelProperties.compartments.priority'
                )}*`}</span>
                <EnhancedField
                    type="number"
                    element="input"
                    value={compartment.priority}
                    onChange={value => onChangeHandler('priority', value)}
                    testId="priority"
                    fieldId="priority"
                    placeholder={t(
                        'masterVehicleModelProperties.compartments.priority'
                    )}
                    theme={theme}
                    error={compartment.getValidationMessage('priority')}
                    required
                />
            </Stack>
            <Box p={2}>
                <span className={theme.label}>{`${t(
                    'masterVehicleModelProperties.compartments.isMandatory'
                )}*`}</span>
                <EnhancedField
                    element="switch"
                    checked={!compartment.optional}
                    onChange={(fieldId, value) =>
                        onChangeHandler('optional', !value)
                    }
                    testId="optional"
                    fieldId="optional"
                    label={
                        !compartment.optional
                            ? t('translations:yes')
                            : t('translations:no')
                    }
                    theme={theme}
                    required
                />
            </Box>
            <CompartmentCapacity
                theme={theme}
                compartment={compartment}
                onChange={onChangeHandler}
                unitConverter={unitConverter}
                t={t}
            />
        </Section>
    );
};

export default VehicleCompartmentInput;
